/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */
import React, {useRef, useState, useEffect, forwardRef, useMemo} from 'react'
import PropTypes from 'prop-types'
import {FormattedMessage, useIntl} from 'react-intl'
import {
    useMultiStyleConfig,
    Box,
    Link as ChakraLink,
    IconButton,
    Badge,
    omitThemingProps,
    StylesProvider,
    Text,
    Tooltip
} from '@chakra-ui/react'

import useBasket from '../../commerce-api/hooks/useBasket'

import {BrandLogo, BasketIcon, HamburgerIcon, StorefinderIcon} from '../icons'
import {STORE_LOCATOR_HREF, CONTACT_US_HREF, HYBRID_CUSTOMER_SERVICE} from '../../constants'

import {noop} from '../../utils/utils'
import useMultiSite from '../../hooks/use-multi-site'
import useEffectEvent from '../../hooks/use-effect-event'
import LocaleSelector from '../locale-selector'
import HybridLink from '../hybrid-link'
import HeaderSearch from './header-search'
import AccountIcon from '../icons/account-icon'
import useCustomer from '../../commerce-api/hooks/useCustomer'

/**
 * The header is the main source for accessing
 * navigation, search, basket, and other
 * important information and actions. It persists
 * on the top of your application and will
 * respond to changes in device size.
 *
 * To customize the styles, update the themes
 * in theme/components/project/header.js
 * @param  props
 * @param   {func} props.onMenuClick click event handler for menu button
 * @param   {func} props.onLogoClick click event handler for menu button
 * @param   {object} props.searchInputRef reference of the search input
 * @param   {func} props.onMyAccountClick click event handler for my account button
 * @param   {func} props.onMyCartClick click event handler for my cart button
 * @return  {React.ReactElement} - Header component
 */
const Header = forwardRef((props, ref) => {
    const intl = useIntl()
    const customer = useCustomer()
    const basket = useBasket()
    const {site, buildUrl} = useMultiSite()
    // there is some bug that scrolls whole page with 1-2 px when opening the header
    const headerHideShowOffset = 5

    const styles = useMultiStyleConfig('Header', props)
    const {
        children,
        onMenuClick = noop,
        onMyAccountClick = noop,
        onLogoClick = noop,
        onMyCartClick = noop,
        onSearchClick,
        menuIcon: MenuIcon = HamburgerIcon,
        ...ownProps
    } = omitThemingProps(props)

    /* When scrolling downwards - hide header, upwards - show header */
    const scrollTop = useRef(0)
    const [hideHeader, setHideHeader] = useState(false)

    const onScroll = useEffectEvent(() => {
        requestAnimationFrame(() => {
            // show header when on top of pdp
            if (scrollY === 0) {
                setHideHeader(false)
                return
            }

            if (
                scrollY > ref?.current?.clientHeight &&
                Math.abs(scrollY - scrollTop.current) > headerHideShowOffset
            ) {
                setHideHeader(scrollY > scrollTop.current)

                scrollTop.current = scrollY
            }
        })
    })
    useEffect(() => {
        addEventListener('scroll', onScroll, {passive: true})

        /* Cleanup event listener */
        return () => removeEventListener('scroll', onScroll)
    }, [onScroll])

    const storeLocatorHref = useMemo(() => buildUrl(STORE_LOCATOR_HREF), [buildUrl])

    return (
        <StylesProvider value={styles}>
            <Box
                bg="stokkeCore.white"
                // this is a workaround to prevent header shifts on modal/drawer open
                paddingInlineEnd="var(--removed-body-scroll-bar-size)"
            >
                <Box
                    ref={ref}
                    {...styles.container}
                    {...ownProps}
                    marginTop={hideHeader && `${-ref?.current?.clientHeight}px`}
                >
                    <Box {...styles.mainContainer}>
                        <IconButton
                            aria-label={intl.formatMessage({
                                id: 'header.button.assistive_msg.logo',
                                defaultMessage: 'Logo'
                            })}
                            icon={<BrandLogo {...styles.logo} />}
                            {...styles.brandButton}
                            variant="unstyled"
                            onClick={onLogoClick}
                        />
                        <Box {...styles.leftContent}>
                            <IconButton
                                aria-label={intl.formatMessage({
                                    id: 'header.button.assistive_msg.menu',
                                    defaultMessage: 'Menu'
                                })}
                                icon={<MenuIcon {...styles.icon} />}
                                variant="unstyled"
                                {...styles.menuButton}
                                onClick={onMenuClick}
                            />
                            <Box {...styles.bodyContainer}>{children}</Box>
                        </Box>
                        <Box {...styles.rightContent}>
                            <HeaderSearch onShow={onSearchClick} />

                            <Tooltip
                                hasArrow
                                label={
                                    customer.isRegistered ? (
                                        <Text textStyle="Regular Medium">
                                            <FormattedMessage
                                                id="global.account.tooltip"
                                                defaultMessage="Hi, {firstName}"
                                                // use the whole customer to avoid re-rendering
                                                values={customer}
                                            />
                                        </Text>
                                    ) : // no label, no tooltip
                                    null
                                }
                                // margin from icon as per design should be 16px
                                gutter={16}
                            >
                                <IconButton
                                    icon={<AccountIcon isActive={customer.isRegistered} />}
                                    variant="unstyled"
                                    {...styles.accountButton}
                                    onClick={onMyAccountClick}
                                    aria-label={intl.formatMessage({
                                        id: 'header.button.assistive_msg.my_account',
                                        defaultMessage: 'My Stokke'
                                    })}
                                    data-testid="header-account-icon"
                                />
                            </Tooltip>

                            {site?.isContentOnlySite ? (
                                <IconButton
                                    as="a"
                                    href={storeLocatorHref}
                                    target="_blank"
                                    rel="noopener"
                                    aria-label={intl.formatMessage({
                                        id: 'header.button.assistive_msg.storefinder',
                                        defaultMessage: 'Storefinder'
                                    })}
                                    icon={
                                        <StorefinderIcon
                                            {...styles.icon}
                                            data-testid="header-storefinder-icon"
                                        />
                                    }
                                    variant="unstyled"
                                    {...styles.iconButton}
                                />
                            ) : (
                                <IconButton
                                    aria-label={intl.formatMessage({
                                        id: 'header.button.assistive_msg.my_cart',
                                        defaultMessage: 'My cart'
                                    })}
                                    icon={
                                        <Box {...styles.cartIconContainer}>
                                            <BasketIcon
                                                {...styles.icon}
                                                data-testid="header-basket-icon"
                                            />
                                            {basket?.loaded && (
                                                <Badge {...styles.cartBadge}>
                                                    {basket.itemAccumulatedCount}
                                                </Badge>
                                            )}
                                        </Box>
                                    }
                                    variant="unstyled"
                                    {...styles.cartButton}
                                    onClick={onMyCartClick}
                                />
                            )}
                        </Box>
                    </Box>
                    <Box {...styles.helperContainer}>
                        <HybridLink
                            page={HYBRID_CUSTOMER_SERVICE}
                            to={CONTACT_US_HREF}
                            size="xs"
                            variant="underlineLink"
                        >
                            <FormattedMessage id="global.help" defaultMessage="Help" />
                        </HybridLink>
                        <ChakraLink
                            href={storeLocatorHref}
                            size="xs"
                            isExternal
                            variant="underlineLink"
                        >
                            <FormattedMessage id="global.stores" defaultMessage="Stores" />
                        </ChakraLink>
                        <LocaleSelector variant="underlineLink" />
                    </Box>
                </Box>
            </Box>
        </StylesProvider>
    )
})

if (process.env.NODE_ENV !== 'production') {
    Header.displayName = 'Header'
}

Header.propTypes = {
    children: PropTypes.node,
    onMenuClick: PropTypes.func,
    onLogoClick: PropTypes.func,
    onMyAccountClick: PropTypes.func,
    onMyCartClick: PropTypes.func,
    onSearchClick: PropTypes.func,
    searchInputRef: PropTypes.oneOfType([
        PropTypes.func,
        PropTypes.shape({current: PropTypes.elementType})
    ])
}

export default Header
